import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import {MatSelectChange} from '@angular/material/select';
import {ReplaySubject} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  takeUntil
} from 'rxjs/operators';

import {DobryAgentAuthor} from '../../interface/dobry-agent-blog-article';
import {DobryAgentAuthorService} from '../../services/dobry-agent-author.service';
import {ListResponse} from '../../interface/response/list-response';

@Component({
  selector: 'app-dict-dobry-agent-blog-authors',
  templateUrl: './dict-dobry-agent-blog-authors.component.html',
  styleUrls: ['./dict-dobry-agent-blog-authors.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DictDobryAgentBlogAuthorsComponent)
    }
  ]
})
export class DictDobryAgentBlogAuthorsComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() label = 'Autor';
  @Input() multiple = false;
  @Input() control: FormControl = null;
  @Input() required = false;
  @Input() onlyActive = true;

  @Output() selectedAuthor: EventEmitter<DobryAgentAuthor>
    = new EventEmitter<DobryAgentAuthor>();

  @Output() selectedAuthors: EventEmitter<DobryAgentAuthor[]>
    = new EventEmitter<DobryAgentAuthor[]>();

  externalControl = true;
  disabled = false;

  allItems: DobryAgentAuthor[] = [];
  items: DobryAgentAuthor[] = [];

  value?: string | string[] = null;

  authorFilterCtrl: FormControl;

  private destroyed$: ReplaySubject<boolean>
    = new ReplaySubject<boolean>(1);

  constructor(
    private authorService: DobryAgentAuthorService,
    private fb: FormBuilder,
  ) {
    this.authorFilterCtrl = this.fb.control('');
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  ngOnInit(): void {
    if (this.control === null) {
      this.control = new FormControl(this.multiple ? [] : '');
      this.externalControl = false;
    }

    this.authorService
      .getAuthorNotCountedListSubject()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: ListResponse<DobryAgentAuthor>) => {
        this.allItems = response.items ?? [];
        this.items = [...this.allItems];

        this.emitSelectedItems();
      });

    this.authorFilterCtrl.valueChanges
      .pipe(
        debounceTime(150),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((value: string | null) => {
        this.filterItems(value);
      });

    this.loadDefaultList();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: string | string[] | null): void {
    if (this.externalControl) {
      return;
    }

    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(event: MatSelectChange): void {
    this.value = event.value;
    this.onChange(this.value);

    this.emitSelectedItems();
  }

  clearValue(): void {
    const value = this.multiple ? [] : '';

    this.control.setValue(value);
    this.writeValue(value);
    this.onChange(value);

    if (this.multiple) {
      this.selectedAuthors.emit([]);
    }
  }

  private loadDefaultList(): void {
    this.authorService.loadNotCountedList(
      this.onlyActive
        ? {active: true}
        : {}
    );
  }

  private filterItems(value: string | null): void {
    const search = (value ?? '')
      .trim()
      .toLocaleLowerCase('pl-PL');

    if (!search) {
      this.items = [...this.allItems];
      return;
    }

    this.items = this.allItems.filter((author: DobryAgentAuthor) => {
      const firstName = author.firstName ?? '';
      const lastName = author.lastName ?? '';

      const fullName = `${firstName} ${lastName}`
        .trim()
        .toLocaleLowerCase('pl-PL');

      return fullName.includes(search);
    });
  }

  private emitSelectedItems(): void {
    if (this.multiple) {
      const selected = this.allItems.filter((author: DobryAgentAuthor) =>
        this.control.value?.includes(author.uuid)
      );

      this.selectedAuthors.emit(selected);
      return;
    }

    const selected = this.allItems.find(
      (author: DobryAgentAuthor) => author.uuid === this.control.value
    );

    if (selected) {
      this.selectedAuthor.emit(selected);
    }
  }
}
